import { useEffect } from 'react';

// Equinox-style text reveal: headings and ledes are split into words, each word
// sits in a clipped "mask", and when the text scrolls into view the words slide
// up into place line by line (headings ripple word by word within a line).
//
// Only static text is split — the targets below never change after render, so
// React never needs to touch the text nodes we wrap. Skipped for guests who
// prefer reduced motion and where IntersectionObserver is missing.

const HEADINGS = [
  '.hero-title',
  '.section-title',
  '.ed-title',
  '.site-footer-hero h2',
  '.inside-house-heading h3',
  '.chapter-index h2',
  '.book-hero-title',
].join(', ');

const LEDES = [
  '.hero-subtitle',
  '.section-subtitle',
  '.around-lede',
  '.curated-hint',
  '.site-footer-hero blockquote',
  '.book-hero-lede',
].join(', ');

// Opening text plays on load; everything else waits for the scroll.
const ON_LOAD = '.hero-title, .hero-subtitle, .book-hero-title, .book-hero-lede';

const DONE = 'tr-split';

function splitWords(el: HTMLElement) {
  const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
  const texts: Text[] = [];
  for (let node = walker.nextNode(); node; node = walker.nextNode()) texts.push(node as Text);

  texts.forEach((text) => {
    const value = text.nodeValue ?? '';
    if (!value.trim()) return;
    const frag = document.createDocumentFragment();
    value.split(/(\s+)/).forEach((part) => {
      if (!part) return;
      if (/^\s+$/.test(part)) {
        frag.appendChild(document.createTextNode(part));
        return;
      }
      const mask = document.createElement('span');
      mask.className = 'tr-mask';
      const word = document.createElement('span');
      word.className = 'tr-word';
      word.textContent = part;
      mask.appendChild(word);
      frag.appendChild(mask);
    });
    text.parentNode?.replaceChild(frag, text);
  });
}

// Stagger by visual line (measured just before playing, so wrapping is final).
function setDelays(el: HTMLElement, heading: boolean) {
  const masks = Array.from(el.querySelectorAll<HTMLElement>('.tr-mask'));
  let line = -1;
  let lastTop = Number.NEGATIVE_INFINITY;
  let inLine = 0;
  masks.forEach((mask) => {
    const top = mask.getBoundingClientRect().top;
    if (top > lastTop + 4) {
      line += 1;
      inLine = 0;
      lastTop = top;
    }
    const delay = heading ? line * 0.1 + inLine * 0.045 : line * 0.08;
    (mask.firstElementChild as HTMLElement).style.setProperty('--tr-d', `${Math.min(delay, 1.2).toFixed(3)}s`);
    inLine += 1;
  });
}

export function useTextReveal(): void {
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.IntersectionObserver !== 'function') return undefined;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return undefined;

    const root = document.documentElement;
    root.classList.add('tr-on');

    const play = (el: HTMLElement) => {
      setDelays(el, el.matches(HEADINGS));
      // Next frame, so the hidden starting position is painted first.
      requestAnimationFrame(() => el.classList.add('tr-in'));
    };

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          io.unobserve(entry.target);
          play(entry.target as HTMLElement);
        });
      },
      { threshold: 0, rootMargin: '0px 0px -8% 0px' },
    );

    const tag = () => {
      document.querySelectorAll<HTMLElement>(`${HEADINGS}, ${LEDES}`).forEach((el) => {
        if (el.classList.contains(DONE) || el.closest('.status-page')) return;
        splitWords(el);
        el.classList.add(DONE);
        if (el.matches(ON_LOAD)) play(el);
        else io.observe(el);
      });
    };

    tag();
    // Chapters and lazy sections mount later.
    let pending = 0;
    const mo = new MutationObserver(() => {
      if (pending) return;
      pending = window.setTimeout(() => {
        pending = 0;
        tag();
      }, 60);
    });
    mo.observe(document.body, { childList: true, subtree: true });

    return () => {
      mo.disconnect();
      io.disconnect();
      if (pending) window.clearTimeout(pending);
      // Leave any split text visible if the hook is torn down.
      document.querySelectorAll('.tr-split').forEach((el) => el.classList.add('tr-in'));
    };
  }, []);
}
